import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CardSetTransformationService, CoreService, DataService, DialogService, FormConfigurationService, SaveDataRequest, SaveFormEvent, SpinnerService } from 'dspx-core';
import * as _ from 'lodash';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from '../../constants/api-endpoints.constants';
import { NAV_TAB_NUMBERS, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, SAVE_RESULT_STATUS, SECTION_CODES, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { CareAllowanceFinancialYearSaveDataRequest } from '../../interfaces/care-allowance-financial-year-save-request.interface';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';

@Component({
  selector: 'app-care-allowance-year',
  templateUrl: './care-allowance-year.component.html',
  styleUrl: './care-allowance-year.component.scss'
})
export class CareAllowanceYearComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getCareAllowanceFinancialYearConfig;

  templateId: number = TEMPLATES.careAllowanceYear;
  versionNo: number = VERSIONS.latest;
  model: any;
  activeDialog: string = '';
  id: string = '';

  private isFinancialYearType = false;
  private hasUnsavedInvoicePeriods = false;

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected appDataService: AppDataService,
    protected dialogService: DialogService,
    private coreService: CoreService,
    private spinnerService: SpinnerService,
    private dataService: DataService,
    private errorService: ErrorService,
    private formConfigurationService: FormConfigurationService,
  ) {
    super(router, appDataService, dialogService);
  }

  ngOnInit(): void {
    this.coreService.applicationFunctions = {
    };

    this.coreService.conditionFunctions = {
      alwaysReadOnly: () => true,
    };

    this.route.paramMap.subscribe((paramMap: ParamMap) => {
      const id = paramMap.get('id');
      if (id) {
        // We are editing a year.
        this.id = id;

        if (this.router.url.includes(ROUTE_CONSTANTS.careAllowanceFinancialYear.prefix)) {
          // It's a financial year.
          this.isFinancialYearType = true;

          if (this.router.url.includes(ROUTE_CONSTANTS.careAllowanceYear.generateInvoicePeriods)) {
            this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowanceFinancialYearConfigAndResponseWithGeneratedInvoicePeriods;
            this.hasUnsavedInvoicePeriods = true;
          }
          else {
            this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowanceFinancialYearConfigAndResponse;
          }
        }
        else {
          // It's for a calendar year.
          if (this.router.url.includes(ROUTE_CONSTANTS.careAllowanceYear.generateInvoicePeriods)) {
            this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowanceCalendarYearConfigAndResponseWithGeneratedInvoicePeriods;
            this.hasUnsavedInvoicePeriods = true;
          }
          else {
            this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowanceCalendarYearConfigAndResponse;
          }
        }
      }
      else {
        // We are creating a new year.
        if (this.router.url.includes(ROUTE_CONSTANTS.careAllowanceFinancialYear.prefix)) {
          // It's a financial year.
          this.isFinancialYearType = true;
          this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowanceFinancialYearConfig;
        }
        else {
          // It's for a calendar year.
          this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowanceCalendarYearConfig;
        }
      }
    });
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded(event: any): void {
    // If this is for a new Financial Year, attempt to load the default values for the Start and End Dates.
    if (this.id === '') {
      if (event.defaultStartDate != null) {
        _.set(
          this.baseForm.model,
          `form.tabs.tab${NAV_TAB_NUMBERS.careAllowanceYearForm.year}` +
          `.page${PAGE_NUMBERS.careAllowanceYearForm.year}` +
          `.${SECTION_CODES.careAllowanceYearForm.yearDetails}` +
          `.${QUESTION_CODES.careAllowanceYearForm.startDate}`,
          event.defaultStartDate
        );
      }

      if (event.defaultEndDate != null) {
        _.set(
          this.baseForm.model,
          `form.tabs.tab${NAV_TAB_NUMBERS.careAllowanceYearForm.year}` +
          `.page${PAGE_NUMBERS.careAllowanceYearForm.year}` +
          `.${SECTION_CODES.careAllowanceYearForm.yearDetails}` +
          `.${QUESTION_CODES.careAllowanceYearForm.endDate}`,
          event.defaultEndDate
        );
      }
    }

    if (this.hasUnsavedInvoicePeriods) {
      this.activeDialog = POP_UP_CODES.careAllowanceYear.invoicePeriodsGenerated;
      this.dialogService.showDialog(this.activeDialog);
    }
  }

  modelChanged(event: any): void {
  }

  filter(event: any): void {
  }

  buttonClicked(event: any): void {
  }

  save(event: SaveFormEvent) {
    const saveDataRequest: CareAllowanceFinancialYearSaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: event.isSubmit,
      saveInvoicePeriods: this.hasUnsavedInvoicePeriods
    }
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  cancel(event: any) {
    this.exit();
  }

  actionButton_invoicePeriodGrid_datagrid_custom_generatePeriods_Clicked(event: any): void {
    if (this.isFinancialYearType) {
      this.navigate(['/', ROUTE_CONSTANTS.careAllowanceFinancialYear.prefix, this.id, ROUTE_CONSTANTS.careAllowanceYear.generateInvoicePeriods], true);
    }
    else {
      this.navigate(['/', ROUTE_CONSTANTS.careAllowanceCalendarYear.prefix, this.id, ROUTE_CONSTANTS.careAllowanceYear.generateInvoicePeriods], true);
    }
  }

  actionButton_invoicePeriodGrid_datagrid_edit_Clicked(event: any): void {
    if (this.isFinancialYearType) {
      this.navigate(['/', ROUTE_CONSTANTS.careAllowanceFinancialYear.prefix, this.id, ROUTE_CONSTANTS.careAllowanceInvoicePeriod.prefix, event.event.data.id], true);
    }
    else {
      this.navigate(['/', ROUTE_CONSTANTS.careAllowanceCalendarYear.prefix, this.id, ROUTE_CONSTANTS.careAllowanceInvoicePeriod.prefix, event.event.data.id], true);
    }
  }

  dialogButton_saveFinancialYearSuccessful_Clicked(event: any): void {
    this.exit(true);
  }

  dialogButton_saveCalendarYearSuccessful_Clicked(event: any): void {
    this.exit(true);
  }

  private exit(ignoreUnsavedChanges = false): void {
    if (this.isFinancialYearType) {
      this.navigate(['/', ROUTE_CONSTANTS.careAllowanceFinancialYear.list], ignoreUnsavedChanges);
    }
    else {
      this.navigate(['/', ROUTE_CONSTANTS.careAllowanceCalendarYear.list], ignoreUnsavedChanges);
    }
  }

  private saveData(request: SaveDataRequest) {
    this.spinnerService.showSpinner();
    const endpoint = this.isFinancialYearType
      ? API_ENDPOINTS.careAllowanceFinancialYear.save
      : API_ENDPOINTS.careAllowanceCalendarYear.save;
    this.registerSubscription(
      this.dataService.saveData(endpoint, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            const errorPopUpCodes = this.isFinancialYearType
              ? SERVER_ERROR_POP_UP_CODES.saveCareAllowanceFinancialYear
              : SERVER_ERROR_POP_UP_CODES.saveCareAllowanceCalendarYear;
            if (errorPopUpCodes.includes(data.message)) {
              this.activeDialog = data.message;
            }
            else {
              this.id = data.id;
              this.activeDialog = this.isFinancialYearType
                ? POP_UP_CODES.careAllowanceFinancialYear.saveFinancialYearSuccessful
                : POP_UP_CODES.careAllowanceCalendarYear.saveCalendarYearSuccessful;
            }
            this.dialogService.showDialog(this.activeDialog);
          }
        })
    );
  }
}
